package dev.danko.roblox_craft_ai.uikit.i18n

import dev.danko.roblox_craft_ai.shared.model.AssetType

object RuStrings : AppStrings {
    override val common = object : CommonStrings {
        override val cancel = "Отмена"
        override val save = "Сохранить"
        override val delete = "Удалить"
        override val retry = "Повторить"
        override val regenerate = "Перегенерировать"
        override val close = "Закрыть"
        override val clear = "Очистить"
        override val copy = "Копировать"
        override val copied = "Скопировано"
        override val confirm = "Подтвердить"
        override val send = "Отправить"
        override val show = "Показать"
        override val hide = "Скрыть"
        override val help = "Помощь"
        override val selectPlaceholder = "Выбери..."
        override val demoBadge = "Демо"
        override val betaBadge = "Бета"
        override val you = "Ты"
        override val language = "Язык"
        override val simpleMode = "Простой режим"
        override val expertMode = "Режим эксперта"
        override val simpleModeHint = "Простой режим скрывает выбор AI-модели и технические детали"
        override fun lines(count: Int) = "$count ${slavicPlural(count, "строка", "строки", "строк")}"
        override fun secondsShort(seconds: Int) = "${seconds}с"
        override fun minutesSeconds(minutes: Int, seconds: Int) = "${minutes}м ${seconds.toString().padStart(2, '0')}с"
        override fun agoSeconds(seconds: Int) = "${seconds}с назад"
        override fun agoMinutes(minutes: Int) = "${minutes}м назад"
    }

    override val assetTypes = object : AssetTypeStrings {
        override fun name(type: AssetType) = when (type) {
            AssetType.LUAU_SCRIPT -> "Luau-скрипт"
            AssetType.MESH_3D -> "3D-модель"
            AssetType.ANIMATION -> "Анимация"
            AssetType.ROJO_PROJECT -> "Rojo-проект"
        }

        override fun generateButton(type: AssetType) = when (type) {
            AssetType.LUAU_SCRIPT -> "Создать скрипт"
            AssetType.MESH_3D -> "Создать 3D-модель"
            AssetType.ANIMATION -> "Создать анимацию"
            AssetType.ROJO_PROJECT -> "Создать Rojo-проект"
        }

        override fun availabilityNote(type: AssetType) = when (type) {
            AssetType.MESH_3D -> "Бета: 3D-модель строится по эскизу или по твоей картинке (скрепка). В Studio модель пока вставляется заготовкой."
            AssetType.ANIMATION -> "Бета: ИИ создаёт анимацию для персонажа R15. Перед отправкой в Studio выдели R15-модель — анимация откроется в Animation Editor."
            else -> null
        }
    }

    override val errors = object : ErrorStrings {
        override val aiUnavailable = "AI сейчас недоступен. Попробуй ещё раз через минуту."
        override val rateLimited = "Слишком много запросов. Подожди немного и попробуй снова."
        override val network = "Нет связи с сервером. Проверь, что бэкенд запущен."
        override val meshTimeout = "3D-модель создавалась слишком долго. Попробуй ещё раз."
        override val meshFailed = "Не получилось создать 3D-модель. Попробуй описать её по-другому."
        override val cancelled = "Генерация отменена."
        override val generic = "Что-то пошло не так. Попробуй ещё раз."
        override val details = "Подробности"
        override val backendDownTitle = "Сервер не запущен"
        override val backendDownHint = "Пока бэкенд выключен, создавать ничего не получится. Запусти его в папке проекта командой ./gradlew :backend:run — страница сама увидит, когда он заработает."
        override val checkAgain = "Проверить снова"
        override val contentBlocked = "Такой запрос выполнить нельзя: он нарушает правила для игр на Roblox. Попробуй описать идею по-другому."
        override val unsafeCodeBlocked = "AI написал код, который нарушает правила безопасности, поэтому мы его не выдали. Попробуй переформулировать запрос."
        override val authRequired = "Чтобы создавать, войди по email — это займёт минуту."
        override val quotaExceeded = "Кредиты закончились. Купи пакет кредитов или выбери тариф на странице «💎 Тарифы»."
        override val budgetExceeded = "Сегодня лимит генераций исчерпан. Попробуй завтра."
    }

    override val promptBox = object : PromptBoxStrings {
        override val label = "Что создадим?"
        override val attachReference = "Прикрепить картинку"
        override fun addMorePhotos(count: Int) = "Добавить картинку ($count)"
        override val placeholder = "Опиши, что нужно создать для Roblox. Можно вставить картинку (Ctrl+V / Cmd+V)..."
        override val referencesLabel = "🖼️ Картинки-примеры:"
        override val assetTypeLabel = "Что создаём"
        override val aiModelLabel = "AI-модель"
        override val autoProvider = "🤖 Авто"
        override val generating = "Создаю..."
        override val tooManyReferences = "⚠️ Можно не больше трёх картинок. Лишние не добавлены"
        override val viewsTitle = "Ракурсы"
        override val viewFront = "Спереди"
        override val viewBack = "Сзади"
        override val viewLeft = "Слева"
        override val viewRight = "Справа"
        override val viewsHint = "Загрузи лист с несколькими видами — разрежу его сам. Или добавь виды по одному в слоты. С 2 и больше видами модель строится сразу по ним, без эскиза."
        override val viewsSplitting = "Ищу ракурсы на картинке…"
        override fun viewsSplit(count: Int) = "Нашёл ракурсов: $count. Проверь, все ли на своих местах"
        override val viewsSwapSides = "Поменять лево и право"
        override val viewsHighOnlyFront = "«Высокое» (TRELLIS 2) берёт только вид спереди. Все ракурсы использует «Обычное» и «Максимальное»."
        override val viewsSwitchToMax = "Переключить на «Максимальное»"
        override val viewsTooMany = "⚠️ Слоты заняты. Убери картинку из слота, чтобы добавить новую"
        override val onlyImages = "⚠️ Подходят только PNG, JPG и WebP"
        override val fileTooLarge = "⚠️ Файл слишком большой, максимум 5 МБ"
        override val dropTitle = "+ Перетащи сюда картинки-примеры"
        override val dropHint = "До 3 файлов, каждый до 5 МБ"
        override val dropAria = "Перетащите сюда картинки-примеры, максимум три"
        override val emptyPrompt = "✏️ Сначала опиши, что нужно создать"
        override fun promptFromImages(type: AssetType) = "${assetTypes.name(type)} для Roblox по прикреплённым картинкам"
    }

    override val assetCard = object : AssetCardStrings {
        override val stagePrompt = "Запрос"
        override val stageAi = "AI"
        override val stageBuild = "Сборка"
        override val stageSync = "Проверка"
        override val stageDone = "Готово"
        override val statusPending = "В очереди"
        override val statusValidating = "Проверка"
        override val statusRetrying = "Исправление"
        override val statusStreaming = "Создаётся"
        override val statusReady = "Готово"
        override val statusFailed = "Ошибка"
        override fun revisionBadge(current: Int, total: Int) = "v$current ($total ${slavicPlural(total, "версия", "версии", "версий")})"
        override fun referencesBadge(count: Int) = "🖼️ $count"
        override fun syncPending(countdown: String) = "🔗 Ждём Studio...$countdown"
        override fun syncDelivering(countdown: String) = "⚙ Отправка...$countdown"
        override val syncSynced = "✅ Уже в Studio!"
        override val syncFailed = "❌ Не удалось отправить"
        override val deleteTooltip = "Удалить"
        override val waitingForPlugin = "🔗 Ждём, когда плагин Roblox Studio заберёт ассет..."
        override val progressValidating = "⚙ Проверяю код..."
        override val progressRetrying = "⚠️ Нашёл ошибку, исправляю..."
        override fun progressTokens(count: Int) = "⚡ Пишу код... ($count)"
        override val progressProcessing = "⏳ Работаю..."
        override val download = "📦 Скачать: "
        override val editPrompt = "✏️ Изменить запрос"
        override val aiChat = "Чат с AI"
        override val downloadZip = "Скачать .ZIP"
        override val synced = "Отправлено!"
        override val retrySync = "Отправить снова"
        override val syncToStudio = "В Roblox Studio"
        override val demoMeshHint = "Это демо-модель: настоящая 3D-модель строится по эскизу или по картинке, когда на сервере подключён генератор 3D."
        override val betaAnimationHint = "Анимации в бета-версии: это демонстрационный результат."
    }

    override val pipeline = object : PipelineStrings {
        override val prompt = "Запрос"
        override val luauPromptDescription = "Запрос принят"
        override val llmStream = "AI пишет код"
        override val llmStreamDescription = "Нейросеть пишет Luau-код"
        override val syntaxCheck = "Проверка"
        override val syntaxCheckDescription = "Проверка синтаксиса и безопасности кода"
        override val ready = "Готово"
        override val luauReadyDescription = "Скрипт можно отправить в Studio"
        override val meshPromptDescription = "Описание 3D-модели"
        override val meshGeneration = "3D-генерация"
        override val meshGenerationDescription = "Создание формы модели"
        override val meshTexturing = "Текстуры"
        override val meshTexturingDescription = "Раскраска и материалы"
        override val meshExport = "Экспорт GLB"
        override val meshExportDescription = "Подготовка файла модели"
        override val meshReadyDescription = "3D-модель готова"
        override val animationPromptDescription = "Описание анимации"
        override val motionAi = "Движение"
        override val motionAiDescription = "Создание ключевых кадров"
        override val rigging = "Скелет R15"
        override val riggingDescription = "Привязка к костям R15"
        override val animationReadyDescription = "Анимация готова (бета)"
        override val scaffoldTree = "Структура"
        override val scaffoldTreeDescription = "Создание файлов Rojo-проекта"
        override val serverServices = "Сервер"
        override val serverServicesDescription = "Серверные сервисы"
        override val clientControllers = "Клиент"
        override val clientControllersDescription = "Клиентские контроллеры и UI"
        override val rojoReady = "Готово"
        override val rojoReadyDescription = "Проект готов для Rojo"
    }

    override val chat = object : ChatStrings {
        override val quickOptimize = "⚡ Ускорить код"
        override val quickStrict = "🛡️ Включить --!strict и типы"
        override val quickBugs = "🐛 Найти ошибки"
        override val quickErrors = "✨ Добавить обработку ошибок"
        override val quickDocs = "📝 Добавить комментарии"
        override fun revisionShort(number: Int) = "Версия $number"
        override fun revisionLong(number: Int) = "Версия №$number"
        override val inputPlaceholder = "Напиши, что изменить или улучшить в скрипте."
        override val aiAssistant = "🤖 AI-помощник"
        override val diffTitle = "Изменения (diff)"
        override val acceptChanges = "Принять изменения"
        override val thinking = "AI изучает код и вносит изменения..."
        override val changesApplied = "✅ Изменения применены"
    }

    override val code = object : CodeStrings {
        override val run = "Запустить"
        override val runViaBackend = "Запустить через бэкенд…"
        override val connectPluginFirst = "Сначала подключи плагин Roblox Studio"
        override fun projectTree(files: Int) = "📁 Файлы проекта ($files)"
        override val filterPlaceholder = "Фильтр..."
        override val undoTooltip = "Отменить последнее действие"
        override val redoTooltip = "Вернуть отменённое действие"
        override val resetTooltip = "Вернуть исходный код"
        override val copyTooltip = "Скопировать код"
        override val downloadTooltip = "Скачать скрипт как .lua"
        override val fullscreenTooltip = "На весь экран"
    }

    override val meshViewer = object : MeshViewerStrings {
        override val shadingTextured = "Текстуры"
        override val shadingClay = "Глина"
        override val shadingWire = "Сетка"
        override val shadingFlat = "Плоско"
        override val lightStudio = "Студия"
        override val lightSun = "Солнце"
        override val lightNeon = "Неон"
        override val cameraIso = "Изо"
        override val cameraFront = "Спереди"
        override val cameraTop = "Сверху"
        override val cameraSide = "Сбоку"
        override val pause = "Пауза"
        override val spin = "Вращать"
        override val exitFullscreen = "Выйти"
        override val fullscreen = "На весь экран"
        override fun stats(triangles: String, vertices: String, dimensions: String) = "📊 $triangles треуг. • $vertices верш. • $dimensions"
        override val controlsHint = "ЛКМ: вращать • ПКМ/Shift: двигать • Колесо: масштаб"
        override val screenshot = "Скриншот"
        override val screenshotTitle = "Скриншот 3D"
        override fun screenshotSaved(filename: String) = "Скриншот модели сохранён: $filename"
        override val animationParseError = "Не удалось прочитать анимацию, персонаж стоит в базовой позе"
        override val animationMissing = "У этой работы нет кадров (старая демо-версия) — создай анимацию заново"
        override val animationPreviewTitle = "Просмотр анимации"
        override val play = "Играть"
        override val stop = "Стоп"
        override val loopOn = "Повтор: ВКЛ"
        override val loopOff = "Повтор: ВЫКЛ"
        override val exportKeyframes = "Экспорт кадров (.json)"
        override fun resizeHint(heightPx: Int) = "↕ Потяни, чтобы изменить высоту (${heightPx}px)"
        override val shadingBaseColor = "Только цвет"
        override val shadingNormals = "Нормали"
        override val shadingUvChecker = "Шахматка UV"
        override val shadingXray = "Рентген"
        override val lightRoblox = "Roblox"
        override val lightNight = "Ночь"
    }

    override val header = object : HeaderStrings {
        override val appTitle = "Craftiva AI"
        override val appSubtitle = "Мастерская Roblox"
        override val presets = "📚 Идеи"
        override val settings = "Настройки"
        override fun aiReady(count: Int) = "AI: подключено $count"
        override val aiDemo = "AI: демо-режим"
        override val pluginConnected = "Плагин: подключён"
        override val pluginPending = "Плагин: ждём"
        override val tokenLabel = "Токен: "
    }

    override val home = object : HomeStrings {
        override val searchWorks = "Найти работу"
        override val allIdeas = "Все идеи →"
        override val open = "Открыть"
        override val moreActions = "Ещё действия"
        override val generateSimilar = "Похожее"
        override val options = "Параметры"
        override val sendToStudio = "Отправить в Roblox Studio"
        override fun creditsCost(credits: Int) = "$credits " + slavicPlural(credits, "кредит", "кредита", "кредитов")
        override val rename = "Переименовать"
        override val renameFailed = "Не удалось переименовать"
        override val noMatches = "Ничего не найдено"
        override val pageTitle = "Craftiva AI — мастерская Roblox"
        override val heroTitle = "Что создадим сегодня?"
        override val heroSubtitle = "Опиши идею — получишь готовый скрипт или модель и отправишь в Roblox Studio одной кнопкой."
        override val samplePrompt = "Сделай деревянный меч"
        override val savePreset = "💾 Сохранить как идею"
        override val addToQueue = "✚ В очередь"
        override val conceptMode = " 🎨 Сначала рисунок, потом 3D"
        override val shiftHint = "💡 Shift + «Создать» = добавить в очередь"
        override val noIdea = "💡 Не знаешь, что создать?"
        override val browsePresets = "📚 Посмотреть идеи"
        override val recentAssets = "Мои работы"
        override fun totalAssets(count: Int) = "(всего $count)"
        override val wideView = "Широко"
        override val gridView = "Сеткой"
        override val noAssetsTitle = "Пока пусто"
        override val noAssetsHint = "Напиши запрос выше, чтобы создать первую работу."
        override fun generationFailed(reason: String) = "Ошибка генерации: $reason"
        override val cancelledByUser = "Генерация отменена"
        override val conceptGenerating = "🎨 Рисую эскиз..."
        override val conceptReady = "✨ Эскиз готов! Посмотри на форму перед 3D."
        override fun conceptFailed(reason: String) = "Ошибка эскиза: $reason"
        override val conceptApproved = "🚀 Эскиз одобрен! Создаю 3D-модель..."
        override fun meshFromConceptFailed(reason: String) = "Ошибка 3D-модели: $reason"
        override val conceptRerolling = "🔄 Рисую другой вариант..."
        override val conceptRerolled = "✨ Новый вариант готов!"
        override fun conceptRerollFailed(reason: String) = "Не получилось перерисовать: $reason"
        override fun presetLoaded(title: String) = "✨ Идея «$title» загружена"
        override fun queued(pending: Int, position: Int) = "✨ В очереди: $pending, позиция №$position"
        override val enterPromptBeforeSaving = "⚠️ Сначала напиши запрос"
        override val personalPresetDescription = "Моя идея"
        override fun presetSaved(title: String) = "💾 Идея «$title» сохранена. Найди её в 📚 Идеи → 👤 Мои"
        override val presetSaveFailed = "Не удалось сохранить"
        override fun cardDeleted(shortId: String) = "🗑️ Работа #$shortId удалена"
        override val regenerating = "🔄 Создаю заново..."
        override fun queuedForStudio(shortId: String) = "Работа #$shortId отправлена в очередь Studio!"
        override val syncBackendUnavailable = "Не удалось связаться с сервером для отправки."
        override val settingsSaved = "Настройки сохранены!"
        override fun refactorFailed(reason: String) = "Ошибка при изменении кода: $reason"
        override val changesApplied = "Изменения применены!"
        override fun applyChangesFailed(reason: String) = "Не удалось применить изменения: $reason"
    }

    override val presetDialog = object : PresetDialogStrings {
        override val title = "💾 Сохранить как свою идею"
        override val nameLabel = "Название"
        override val namePlaceholder = "Мой крутой огненный эффект (3–60 символов)"
        override val nameTooShort = "Нужно хотя бы 3 символа"
        override fun nameCounter(length: Int) = "OK · $length/60"
        override val descriptionLabel = "Описание (необязательно)"
        override val descriptionPlaceholder = "Коротко, для чего этот запрос (до 140 символов)"
        override val tagsLabel = "Теги (необязательно)"
        override val tagsPlaceholder = "огонь, магия, меч — через запятую"
        override fun tagsCounter(count: Int) = "До 6 тегов · $count"
        override val currentPrompt = "Текущий запрос (будет сохранён):"
        override val emptyPrompt = "(пусто — сначала напиши запрос)"
        override fun typeAndLimit(typeName: String) = "Тип: $typeName · максимум 50 своих идей"
    }

    override val queue = object : QueueStrings {
        override val statusPending = "Ждёт"
        override val statusRunning = "В работе"
        override val statusDone = "Готово"
        override val statusFailed = "Ошибка"
        override val statusCancelled = "Отменено"
        override fun title(size: Int) = "📦 Очередь ($size)"
        override fun eta(minutes: Int, seconds: Int) = " ⏱️ Осталось ~${minutes}м ${seconds.toString().padStart(2, '0')}с"
        override val resume = "▶️ Продолжить"
        override val pause = "⏸️ Пауза"
        override val resumed = "Очередь продолжена"
        override val paused = "Очередь на паузе"
        override val pausedBadge = " ⏸️ На паузе"
        override val cleared = "Готовые задачи убраны"
        override val empty = "🕳️ Пока нет задач. Shift + «Создать» добавит задачу в очередь."
        override val scrollTip = "💡 Если задач больше 30, список прокручивается"
        override val taskCancelled = "Задача отменена"
        override fun position(position: Int) = "позиция $position"
        override val startCallbackMissing = "Очередь ещё не готова"
        override val generationFailed = "Ошибка генерации"
        override val unexpectedError = "Неожиданная ошибка"
    }

    override val concept = object : ConceptStrings {
        override val stepTitle = "1. Эскиз (2D)"
        override val stepHint = "Проверь форму и силуэт перед созданием 3D-модели"
        override val savingBadge = "💎 Экономит 3D-генерации"
        override val imageAlt = "Эскиз"
        override fun promptExpansion(expanded: Boolean) = "🔍 Подробный запрос для художника (${if (expanded) "свернуть" else "показать"})"
        override val reconstructing = "⏳ Создаю 3D..."
        override val approve = "✅ Нравится, делай 3D"
        override val regenerating = "↻ Рисую..."
        override val regenerate = "🔄 Нарисовать другой"
    }

    override val health = object : HealthStrings {
        override val unknown = "⚪ Статус неизвестен"
        override val allOk = "🌿 Все системы в порядке"
        override val critical = "🔴 Критическая ошибка"
        override fun degraded(component: String) = "🟡 Проблемы: $component"
        override val degradedGeneric = "🟡 Есть проблемы"
        override val title = "Состояние системы"
        override fun updatedAt(time: String) = "Обновлено: $time"
    }

    override val sessionToken = object : SessionTokenStrings {
        override val validationFailed = "Не удалось проверить токен. Убедись, что он совпадает с токеном в плагине Roblox Studio."
        override val title = "🔗 Подключить плагин Roblox Studio"
        override val saveAndLink = "Сохранить и подключить"
        override val instructions = "💡 Скопируй токен этого сайта и вставь его в окно плагина Craftiva в Roblox Studio — так твои работы будут попадать прямо в Studio."
        override val tokenLabel = "Токен сессии"
        override val tokenPlaceholder = "например RK_LIVE_7AB3C9EF"
        override val pasteFromClipboard = "Вставить из буфера"
        override val generateNew = "Создать новый"
    }

    override val syncModal = object : SyncModalStrings {
        override val untitledAsset = "(без названия)"
        override val title = "⚠️ Отправить в Roblox Studio?"
        override val confirm = "Да, отправить"
        override val warning = "⚠️ Работа появится в твоём Roblox Studio при следующей синхронизации плагина (каждые 3 секунды). Скрипты вставляются выключенными — проверь код и нажми «Включить» в окне плагина. Всё можно отменить через Ctrl+Z."
        override val averageDelay = "⏱️ Обычно это занимает 2–3 секунды."
        override val dontShowAgain = "Больше не спрашивать (не рекомендуется)"
    }

    override val revisions = object : RevisionStrings {
        override val title = "👁️ Код и история версий"
        override val listLabel = "Версии:"
        override fun restore(number: Int) = "Вернуть v$number"
        override fun restored(number: Int) = "Возвращена версия v$number"
        override fun latest(number: Int) = "Последняя (v$number)"
    }

    override val settingsRedirect = object : SettingsRedirectStrings {
        override val deprecatedNotice = "Используй страницу /settings вместо этого окна."
        override val redirecting = "Переход..."
        override val openingNewPage = "Открывается страница настроек"
        override val movedNotice = "Настройки переехали на отдельную страницу /settings"
        override val openSettings = "Открыть настройки →"
    }

    override val settingsPage = object : SettingsPageStrings {
        override val tabAccount = "👤 Аккаунт"
        override val signedOutTitle = "Ты не вошёл в аккаунт"
        override val signedOutHint = "Войди по email, чтобы видеть тариф и кредиты и пользоваться сервисом с разных устройств."
        override val advanced = "Дополнительно"
        override val dangerZone = "Сброс"
        override val resetSessionHint = "Удаляет все ключи и настройки этой сессии. Работы на сервере останутся."
        override val pageTitle = "Craftiva AI — Настройки"
        override val subtitle = "Панель настроек"
        override val hashLabel = "Хеш: "
        override val backToStudio = "← В мастерскую"
        override val cabinetTitle = "⚙️ Настройки"
        override val cabinetSubtitle = "Аккаунт, плагин Roblox Studio и предпочтения"
        override val resetSession = "Сбросить сессию"
        override val resetSessionConfirm = "Удалить все ключи и настройки этой сессии?"
        override val sessionReset = "Сессия сброшена"
        override val tabKeys = "🔑 API-ключи"
        override val tabGeneral = "⚙️ Общие"
        override val tabPlugin = "🔌 Плагин"
        override val tabAssets = "📦 Работы"
        override val keyAdded = "Ключ добавлен ✓"
        override val keyAddFailed = "Не удалось добавить ключ"
        override val keyUpdated = "Ключ обновлён"
        override val keyUpdateFailed = "Не удалось обновить ключ"
        override val keyDeleted = "Ключ удалён"
        override val keyDeleteFailed = "Не удалось удалить ключ"
        override val priorityChanged = "Приоритет изменён"
        override val priorityChangeFailed = "Не удалось изменить приоритет"
        override val settingsSaved = "Настройки сохранены ✓"
        override val saveFailed = "Не удалось сохранить"
        override val resetDone = "Настройки сброшены"
        override val resetFailed = "Не удалось сбросить"
        override val pluginSettingsSaved = "Настройки плагина сохранены"
        override val connectionOkEmpty = "Соединение есть (в очереди пусто)"
        override fun connectionOkQueued(count: Int) = "Соединение есть (в очереди: $count)"
        override val connectionFailed = "Не удалось связаться с сервером синхронизации"
        override val assetRemovedFromCache = "Работа удалена из кэша браузера"
        override val keysTitle = "Управление API-ключами"
        override val keysManagedByServerTitle = "🔐 Ключи управляются на сервере"
        override val keysManagedByServerHint = "Ключи провайдеров задаёт администратор сервера через переменные окружения или секреты. Изменить их из интерфейса нельзя."
        override fun keysSummary(total: Int, active: Int) = "Всего ключей: $total · Активных: $active"
        override val refresh = "Обновить"
        override val catalogMissing = "Каталог провайдеров не загружен"
        override val catalogMissingHint = "Проверь, что бэкенд запущен и доступен"
        override val providersHeader = "🤖 AI- и 3D-провайдеры"
        override val noModels = "(нет доступных моделей — добавь ключ)"
        override val generalTitle = "Общие настройки"
        override val defaultModel = "AI-модель по умолчанию"
        override val selectModel = "Выбери модель..."
        override val defaultAssetType = "Тип работы по умолчанию"
        override val skipSyncConfirm = "Не спрашивать перед отправкой в Studio"
        override val skipSyncConfirmHint = "Готовые работы будут сразу отправляться в Roblox Studio"
        override val themeSystem = "🌓 Как в системе"
        override val themeLight = "☀️ Светлая"
        override val themeDark = "🌙 Тёмная"
        override val themeLabel = "Тема"
        override val languageLabel = "Язык интерфейса"
        override val reset = "Сбросить"
        override val pluginSettingsTitle = "Настройки плагина Roblox Studio"
        override val pluginTokenTitle = "Токен сессии для плагина"
        override val tokenCopied = "Токен скопирован"
        override val sessionHashTitle = "Хеш сессии (ID хранилища на бэкенде)"
        override val pluginPort = "Порт плагина"
        override val pluginPortHint = "Адрес сервера задаётся в окне плагина в Roblox Studio"
        override val autoConnect = "Подключаться автоматически при запуске"
        override val autoConnectHint = "Плагин начнёт синхронизацию сразу после открытия Studio"
        override val studioPath = "Путь установки Roblox Studio (необязательно)"
        override val testConnection = "Проверить соединение"
        override val howToConnectTitle = "📚 Как подключить плагин?"
        override val howToConnectSteps = listOf(
            "1. Скачай плагин кнопкой ниже — адрес сервера в нём уже указан.",
            "2. Положи файл в папку плагинов Roblox Studio (в Studio: Plugins → Plugins Folder) и перезапусти Studio.",
            "3. Открой панель Craftiva AI, вставь токен сессии сверху и нажми «Сохранить и подключить». Разреши Studio доступ к серверу, когда появится запрос.",
            "4. Готовые работы появятся в Studio выключенными: проверь код и нажми «Включить». Для анимации сначала выдели R15-модель. Ctrl+Z отменяет вставку."
        )
        override val downloadPlugin = "Скачать плагин"
        override val myAssetsTitle = "Мои работы (кэш браузера)"
        override fun assetsSummary(total: Int, shown: Int) = "Всего: $total · Показано: $shown"
        override val filterAll = "Все"
        override val filterReady = "Готовые"
        override val filterProcessing = "В работе"
        override val filterFailed = "Ошибки"
        override val deselectAll = "Снять всё"
        override val selectAll = "Выбрать всё"
        override fun export(selected: Int) = if (selected > 0) "Экспорт ($selected)" else "Экспорт всех"
        override fun deleteSelected(count: Int) = "Удалить ($count)"
        override fun deleteAssetsConfirm(count: Int) = "Удалить из кэша работ: $count?"
        override val deleted = "Удалено"
        override val noAssets = "Работ пока нет"
        override val noAssetsHint = "Создай первую работу на главной странице"
        override val goToStudio = "Перейти в мастерскую →"
    }

    override val providerCard = object : ProviderCardStrings {
        override val defaultLabel = "Основной"
        override fun type(name: String) = "Тип: $name"
        override val active = "✓ Активен"
        override val notConfigured = "Не настроен"
        override fun addKeyFor(provider: String) = "+ Добавить ключ для $provider"
        override fun addKeyTitle(provider: String) = "Добавить ключ $provider"
        override val labelField = "Название ключа"
        override val labelPlaceholder = "Основной, Рабочий и т.д."
        override val apiKeyField = "API-ключ"
        override val apiKeyPlaceholder = "Вставь API-ключ..."
        override fun keyPatternWarning(provider: String) = "⚠️ Ключ $provider обычно выглядит иначе"
        override val keyTooShort = "Ключ слишком короткий (минимум 8 символов)"
        override val baseUrlOptional = "Base URL (необязательно)"
        override fun baseUrlDefault(url: String) = "По умолчанию: $url"
        override val test = "Проверить"
        override val getApiKey = "Получить API-ключ ↗"
        override fun editKeyTitle(provider: String) = "Изменить ключ $provider"
        override val labelShort = "Название"
        override val baseUrlEmptyDefault = "Base URL (пусто = по умолчанию)"
        override val keyHiddenNotice = "🔒 Ключ сохранён и не показывается. Чтобы заменить его, создай новый ключ."
        override fun deleteKeyTitle(label: String) = "Удалить ключ «$label»?"
        override val deleteIrreversible = "Это нельзя отменить. Ключ будет удалён из настроек навсегда."
        override fun providerKeyLine(provider: String, key: String) = "⚠️ Провайдер: $provider · Ключ: $key"
        override fun secondsLeft(seconds: Int) = "(${seconds}с)"
    }

    override val presetsPage = object : PresetsPageStrings {
        override val pageTitle = "Craftiva AI — Идеи"
        override val subtitle = "📚 Библиотека идей"
        override val toStudio = "🎮 В мастерскую"
        override val categories = "Категории"
        override val categoryAll = "Все"
        override val categoryEnvironment = "Окружение"
        override val categoryNpc = "Персонажи и NPC"
        override val categoryInterior = "Интерьер и мебель"
        override val categoryWeapon = "Оружие и инструменты"
        override val categoryItem = "Предметы"
        override val categoryAnimation = "Анимации и эффекты"
        override val categoryScript = "Скрипты"
        override val categoryPersonal = "Мои"
        override val categoryOther = "Другое"
        override val allCategories = "Все категории"
        override val difficultyEasy = "Легко"
        override val difficultyMedium = "Средне"
        override val difficultyHard = "Сложно"
        override val searchPlaceholder = "🔍 Поиск по названию, описанию, тегам..."
        override fun found(count: Int) = "Найдено: $count"
        override val noPersonalTitle = "У тебя пока нет своих идей"
        override val noPersonalHint = "Сохрани удачный запрос на главной странице — кнопка «💾 Сохранить как идею»."
        override val goToStudio = "🎮 Перейти в мастерскую"
        override fun nothingFound(query: String) = "Ничего не найдено по запросу «$query»"
        override val nothingFoundHint = "Попробуй другой запрос или выбери другую категорию."
        override val deleteTitle = "Удалить идею?"
        override fun deleted(title: String) = "Идея «$title» удалена"
        override fun deleteConfirm(title: String) = "Идея «$title» будет удалена навсегда. Продолжить?"
        override val usePrompt = "Использовать"
    }

    override val nav = object : NavStrings {
        override val menu = "Меню"
        override val on = "Вкл"
        override val off = "Выкл"
        override fun plan(name: String) = "Тариф $name"
        override val studioConnected = "Плагин Roblox Studio подключён"
        override val studioNotConnected = "Плагин Roblox Studio не подключён"
        override val workshop = "🎮 Мастерская"
        override val home = "Главная"
        override val about = "О проекте"
        override val toggleColorMode = "Сменить тему"
        override val builtWith = "Сделано на "
        override val templateBy = ", шаблон от "
    }

    override val report = object : ReportStrings {
        override val button = "Пожаловаться"
        override val title = "🚩 Пожаловаться на работу"
        override val hint = "Расскажи, что не так. Жалобу увидит администратор."
        override val reasonLabel = "Причина"
        override val reasonInappropriate = "Неподходящее содержимое"
        override val reasonUnsafeCode = "Опасный или вредный код"
        override val reasonCopyright = "Нарушение авторских прав"
        override val reasonOther = "Другое"
        override val commentLabel = "Комментарий (необязательно)"
        override val commentPlaceholder = "Что именно не так?"
        override val submit = "Отправить жалобу"
        override val thanks = "Спасибо! Жалоба отправлена."
        override val failed = "Не удалось отправить жалобу. Попробуй позже."
    }

    override val auth = object : AuthStrings {
        override val signIn = "Войти"
        override val signOut = "Выйти"
        override val signedOut = "Ты вышел из аккаунта"
        override val accountTooltip = "Аккаунт"
        override val dialogTitle = "Вход по email"
        override val dialogHint = "Мы пришлём письмо со ссылкой для входа. Пароль не нужен."
        override val emailLabel = "Email"
        override val emailPlaceholder = "you@example.com"
        override val sendLink = "Прислать ссылку"
        override val sentTitle = "📬 Проверь почту"
        override fun sentHint(email: String) = "Мы отправили ссылку на $email. Она действует 15 минут. Если письма нет, загляни в «Спам»."
        override val useAnotherEmail = "Другой email"
        override val invalidEmail = "Проверь адрес: похоже, в нём ошибка."
        override val throttled = "Слишком много писем. Подожди 15 минут и попробуй снова."
        override val sendFailed = "Не удалось отправить письмо. Попробуй позже."
        override val verifyTitle = "Вход в Craftiva AI"
        override val verifying = "Проверяю ссылку…"
        override fun verifySuccess(email: String) = "Готово! Ты вошёл как $email."
        override val verifyFailed = "Ссылка не работает: она устарела или уже использована."
        override val requestNewLink = "Запросить новую ссылку"
        override val toWorkshop = "В мастерскую →"
        override fun creditsTooltip(left: Int, limit: Int, purchased: Int, resetsOn: String) = "Месячных: $left из $limit (обновятся $resetsOn). Купленных: $purchased"
    }

    override val billing = object : BillingStrings {
        override val navPricing = "💎 Тарифы"
        override val pageTitle = "Craftiva AI — Тарифы"
        override val title = "Тарифы и кредиты"
        override val subtitle = "Каждое действие стоит кредиты: скрипт — 1, 3D-модель — 10. Месячные кредиты обновляются каждый месяц, купленные не сгорают."
        override val perMonth = "/мес"
        override fun creditsPerMonth(credits: Int) = "$credits кредитов в месяц"
        override val unlimitedCredits = "Без ограничений"
        override val currentPlan = "Твой тариф"
        override val subscribe = "Оформить"
        override val switchPlan = "Перейти"
        override val free = "Бесплатно"
        override val packsTitle = "Пакеты кредитов"
        override val packsHint = "Разовая покупка. Кредиты не сгорают и тратятся после месячных."
        override fun packCredits(credits: Int) = "$credits кредитов"
        override val buy = "Купить"
        override val manageTitle = "Управление подпиской"
        override val cancelSubscription = "Отменить подписку"
        override val updatePayment = "Сменить способ оплаты"
        override fun renewsOn(date: String) = "Продлится $date"
        override val pastDueHint = "Не удалось списать оплату. Обнови способ оплаты, чтобы тариф не отключился."
        override val paymentSoon = "Оплата скоро появится"
        override val signInToBuy = "Войди, чтобы купить"
        override val checkoutDone = "Оплата прошла! Тариф и кредиты обновятся через несколько секунд."
        override val checkoutFailed = "Не удалось открыть оплату. Попробуй позже."
        override val creditCostsTitle = "Сколько стоят действия"
        override fun balance(monthlyLeft: String, purchased: Int) = "Осталось: $monthlyLeft месячных + $purchased купленных"
    }

    override val admin = object : AdminStrings {
        override val nav = "🛠 Админка"
        override val pageTitle = "Craftiva AI — Админка"
        override val title = "Админка"
        override val tabOverview = "📊 Обзор"
        override val tabPlans = "💳 Тарифы"
        override val tabPacks = "⚡ Пакеты"
        override val tabUsers = "👥 Пользователи"
        override val tabModeration = "🛡️ Модерация"
        override val tabAudit = "📜 Журнал"
        override val days7 = "7 дней"
        override val days30 = "30 дней"
        override val totalCost = "Расход всего"
        override val anonymousCost = "Без входа"
        override val topUsers = "Самые затратные пользователи"
        override val byDay = "По дням"
        override val events = "событий"
        override val noData = "Пока нет данных"
        override val name = "Название"
        override val monthlyCredits = "Кредитов в месяц"
        override val credits = "Кредитов"
        override val price = "Цена (центы)"
        override val currency = "Валюта"
        override val paddlePriceId = "Paddle Price ID"
        override val active = "Активен"
        override val unlimitedHint = "Пусто — без лимита"
        override val save = "Сохранить"
        override val saved = "Сохранено"
        override val priceWarning = "Сумма списания задаётся ценой в Paddle. Меняя цену, создайте новую цену в Paddle и впишите её Price ID сюда."
        override val addPack = "Добавить пакет"
        override val packId = "ID пакета (a-z, 0-9, _)"
        override val searchUsers = "Поиск по email…"
        override val email = "Email"
        override val plan = "Тариф"
        override val creditsMonth = "Кредиты за месяц"
        override val purchased = "Купленные"
        override val cost30d = "$ за 30 дней"
        override val status = "Статус"
        override val registered = "Регистрация"
        override val lastLogin = "Последний вход"
        override val creditsDelta = "Кредиты (+ начислить / − списать)"
        override val note = "Комментарий"
        override val grant = "Применить"
        override val granted = "Кредиты изменены"
        override val block = "Заблокировать"
        override val unblock = "Разблокировать"
        override val statusChanged = "Статус изменён"
        override val noUsers = "Пользователи не найдены"
        override val onlyOpen = "Только открытые"
        override val resolve = "Рассмотрено"
        override val resolved = "Отмечено как рассмотренное"
        override val kind = "Тип"
        override val category = "Категория"
        override val excerpt = "Фрагмент"
        override val noIncidents = "Инцидентов нет"
        override val whenLabel = "Когда"
        override val admin = "Кто"
        override val action = "Действие"
        override val target = "Объект"
        override val day = "День"
        override val user = "Пользователь"
        override val cost = "Расход"
        override val providers = "Провайдеры"
        override val details = "Подробности"
        override val totalEvents = "Всего событий"
        override fun saveFailed(reason: String) = "Не удалось сохранить: $reason"
    }

    override val panel = object : AssetPanelStrings {
        override val tabCode = "Код"
        override val tabChat = "Чат с AI"
        override val tabVersions = "Версии"
        override val tabRequest = "Запрос"
        override val tabFiles = "Файлы"
        override val tab3d = "3D"
        override val tabPrep = "Скрипт подготовки"
        override val tabSketch = "Эскиз"
        override val tabAnimation = "Анимация"
        override val downloadGlb = "Скачать .glb"
        override val downloadLua = "Скачать .lua"
        override val validationOk = "Проверено: ошибок нет"
        override val technical = "Технические детали"
        override val whatToChange = "Что поменять? Например: закрывать дверь через 3 секунды"
        override val change = "Изменить"
        override val currentVersion = "Текущая"
        override val references = "Картинки-примеры"
        override val noVersions = "Пока одна версия. Новые появляются после правок в чате."
        override fun version(n: Int) = "версия $n"
        override fun validationIssues(n: Int) = "Замечаний: $n"
    }

    override val tour = object : TourStrings {
        override val step1Title = "Опиши идею"
        override val step1Text = "Напиши, что нужно: скрипт, 3D-модель или анимацию. Можно приложить картинку-пример скрепкой."
        override val step2Title = "Получи результат"
        override val step2Text = "AI создаст работу за несколько секунд. Открой её, чтобы посмотреть код или модель и попросить правки."
        override val step3Title = "Отправь в Roblox Studio"
        override val step3Text = "Подключи плагин в настройках — и готовые работы будут появляться в Studio одной кнопкой."
        override val next = "Далее"
        override val back = "Назад"
        override val start = "Начать"
        override val skip = "Пропустить"
        override fun stepOf(step: Int, total: Int) = "Шаг $step из $total"
    }

    override val legal = object : LegalStrings {
        override val terms = "Условия использования"
        override val privacy = "Конфиденциальность"
        override val refunds = "Возвраты"
        override val notAffiliated = "Не связано с Roblox Corporation. Roblox — товарный знак Roblox Corporation."
        override val loginConsent = "Входя, вы принимаете Условия использования и Политику конфиденциальности. Сервисом можно пользоваться с 13 лет."
        override val checkoutConsent = "Покупки — только для взрослых (18+). Оплату проводит Paddle. Оплачивая, вы принимаете Условия использования и Политику возвратов."
        override val backToSite = "← На сайт"
    }

    override val access = object : AccessStrings {
        override val badge = "Закрытое тестирование"
        override val title = "Создавай игры для Roblox с помощью ИИ"
        override val subtitle = "Опиши идею словами — Craftiva AI напишет Luau-скрипт, сделает 3D-модель или анимацию и отправит всё в Roblox Studio. Сейчас мы тестируем сервис с небольшой группой пользователей."
        override val f1Title = "Скрипты на Luau"
        override val f1Text = "Двери, оружие, магазины, системы урона — готовый проверенный код по твоему описанию."
        override val f2Title = "3D-модели"
        override val f2Text = "Нарисуй концепт или опиши предмет — получишь модель, готовую для Roblox."
        override val f3Title = "Прямо в Studio"
        override val f3Text = "Плагин Craftiva переносит результат в твою игру одним кликом."
        override val formTitle = "Запросить доступ"
        override val formHint = "Оставь заявку — мы откроем доступ и пришлём письмо, когда можно будет войти."
        override val nameLabel = "Как тебя зовут"
        override val namePlaceholder = "Имя или ник"
        override val messageLabel = "Что хочешь создавать"
        override val messagePlaceholder = "Например: свою obby с ловушками и магазином"
        override val ageConfirm = "Мне 13 лет или больше (если меньше 16 и я живу в ЕС — с согласия родителей)"
        override val privacyAccept = "Я принимаю Политику конфиденциальности"
        override val submit = "Отправить заявку"
        override val sentTitle = "Заявка отправлена!"
        override fun sentText(email: String) = "Мы напишем на $email, как только откроем доступ. Обычно это занимает 1–2 дня."
        override val alreadyApproved = "Уже одобрили?"
        override val signIn = "Войти"
        override val notApproved = "Для этого email доступ ещё не открыт. Оставь заявку на странице сервиса — мы пришлём письмо после одобрения."
        override val consentRequired = "Подтверди возраст и согласие с политикой конфиденциальности"
        override val sendFailed = "Не удалось отправить заявку. Попробуй ещё раз."
        override val adminTab = "Заявки"
        override val pending = "Ожидают"
        override val approved = "Одобрены"
        override val rejected = "Отклонены"
        override val all = "Все"
        override val approve = "Одобрить"
        override val reject = "Отклонить"
        override val noRequests = "Заявок нет"
        override val approvedToast = "Доступ открыт, письмо отправлено"
        override val rejectedToast = "Заявка отклонена"
    }

    override val meshInspector = object : MeshInspectorStrings {
        override val reliefMap = "Карта рельефа"
        override val reliefYes = "есть — мелкие детали на свету"
        override val reliefNo = "нет"
        override val relief = "Рельеф"
        override val reliefHint = "Мелкие детали детальной модели, перенесённые в текстуру (так их увидит Roblox)"
        override fun number(value: Int) = groupDigits(value, " ")
        override val tabInfo = "Инфо"
        override val tabView = "Вид"
        override val tabTools = "Инструменты"
        override fun studs(value: Double): String {
            val n = shortDecimal(value, comma = true)
            return if (isWhole(value)) "$n ${slavicPlural(kotlin.math.round(value).toInt(), "студ", "студа", "студов")}" else "$n студа"
        }
        override val readinessTitle = "Готовность к Roblox"
        override val readyAll = "Готово для Roblox"
        override fun readyIssues(count: Int) = "Есть что поправить: $count"
        override fun checkTriangles(count: Int, max: Int) = "Треугольники: ${groupDigits(count, " ")} из ${groupDigits(max, " ")}"
        override val checkTrianglesOver = "Больше лимита MeshPart — Studio не примет модель"
        override fun checkTexture(size: Int) = "Текстура ${size}×$size"
        override val checkTextureNone = "Без текстуры — цвета хранятся в вершинах"
        override val checkTextureOver = "Roblox уменьшит текстуру до 1024 — лучше сжать заранее"
        override val checkFrontAuto = "Перед модели повёрнут к Roblox-фронту (−Z) автоматически"
        override val checkFrontUser = "Перед модели выбран тобой"
        override val checkFrontUnsure = "Не уверены, где у модели перед — посмотри вид «Спереди»"
        override fun checkSize(dimensions: String) = "Размер: $dimensions"
        override val checkSizeTiny = "Очень маленькая — в игре её будет трудно разглядеть"
        override val checkSizeHuge = "Очень большая — проверь, что так и задумано"
        override val checkPartsOk = "Цельная модель, без оторванных кусочков"
        override fun checkParts(small: Int) = "Оторванные кусочки: $small — похоже на мусор генерации"
        override val checkPivotOk = "Стоит на полу, центр внизу"
        override val checkPivotOff = "Не стоит на полу — в Studio модель повиснет или утонет"
        override val checking = "Проверяю…"
        override val fixSimplify = "Упростить"
        override val fixTexture = "Сжать"
        override val fixRotate = "Повернуть"
        override val fixSize = "Задать размер"
        override val fixDebris = "Убрать мусор"
        override val fixPivot = "Поставить на пол"
        override val geometry = "Геометрия"
        override val triangles = "Треугольники"
        override val vertices = "Вершины"
        override val parts = "Отдельные части"
        override val size = "Размер"
        override val fileSize = "Файл"
        override fun megabytes(bytes: Long) = if (bytes < 1024 * 1024) "${shortDecimal(bytes / 1024.0, true)} КБ" else "${shortDecimal(bytes / 1048576.0, true)} МБ"
        override val texture = "Текстура"
        override fun pixels(size: Int) = "${size}×$size px"
        override val noTexture = "нет"
        override val origin = "Откуда модель"
        override val provider = "Генератор"
        override val created = "Создана"
        override val version = "Версия"
        override val sketch = "Эскиз"
        override val mode = "Режим"
        override val light = "Свет"
        override val background = "Фон"
        override val bgDark = "Тёмный"
        override val bgLight = "Светлый"
        override val bgGrey = "Серый"
        override val bgBaseplate = "Бейсплейт"
        override val reference = "Аватар R15 рядом"
        override val referenceHint = "Сравни размер модели с обычным персонажем Roblox (5,2 студа)"
        override val bounds = "Габариты в студах"
        override val grid = "Сетка пола"
        override val spin = "Вращение"
        override val screenshot = "Скриншот"
        override val screenshotTransparent = "Без фона (PNG)"
        override val video = "Видео вращения"
        override val videoRecording = "Записываю видео…"
        override val videoUnsupported = "Этот браузер не умеет записывать видео"
        override val toolsNote = "Каждое изменение сохраняется новой версией — к старой можно вернуться."
        override val budget = "Число треугольников"
        override val budgetMobile = "Телефоны"
        override val budgetNormal = "Обычно"
        override val budgetDetailed = "Детально"
        override fun trianglesValue(count: Int) = "${groupDigits(count, " ")} треуг."
        override val apply = "Применить"
        override val sizeTitle = "Размер в студах"
        override val sizeHint = "Самая длинная сторона модели"
        override val rotateTitle = "Повернуть перед"
        override val rotateHint = "Если модель стоит в Studio боком или спиной"
        override val rotateLeft = "↺ 90°"
        override val rotateRight = "↻ 90°"
        override val rotate180 = "180°"
        override val textureTitle = "Размер текстуры"
        override val debrisTitle = "Убрать оторванные кусочки"
        override val debrisHint = "Мелкие висящие в воздухе части, которые оставил генератор"
        override val downloadTitle = "Скачать"
        override val downloadGlb = "GLB"
        override val downloadObj = "OBJ + текстура"
        override val versions = "Версии"
        override fun versionLabel(number: Int) = "v$number"
        override val current = "сейчас"
        override val restore = "Вернуть"
        override val compare = "Сравнить"
        override val closeCompare = "Закрыть сравнение"
        override val originalVersion = "Как сгенерировано"
        override fun editTriangles(count: Int) = "сетка на ${groupDigits(count, " ")} треуг."
        override val editHighPoly = "детальная модель (High-Poly)"
        override fun budgetFromSource(sourceTriangles: Int?) = "Детальная модель генератора${sourceTriangles?.let { " (" + groupDigits(it, " ") + " треуг.)" } ?: ""} хранится 30 дней после создания: можно и уменьшить, и увеличить число треугольников."
        override val downloadHighPoly = "High-Poly (GLB)"
        override val downloadSharp = "GLB · текстура 2K"
        override val rigTitle = "Скелет для анимации"
        override val rigHint = "Ставит в модель 15 костей как у R15 (бесплатно). Лучше всего для человечков, стоящих прямо. Потом выбери модель как персонажа в настройках анимации."
        override val rigApply = "Поставить кости"
        override val rigDone = "Кости поставлены: модель можно выбрать как персонажа анимации."
        override val rigDownload = "GLB со скелетом"
        override val rigWorking = "Ставлю кости…"
        override fun rigPro(cost: String?) = "Точнее — Meshy" + (cost?.let { " · $it" } ?: "")
        override val rigProHint = "Meshy ставит 24 кости и аккуратнее распределяет кожу; только для человечков, около минуты."
        override val rigByMeshy = "Кости от Meshy"
        override val preparingSharp = "Готовлю модель с текстурой 2K — это займёт до минуты."
        override val retextureTitle = "Сменить текстуру"
        override val retextureHint = "Форма и размер останутся, поменяются цвета и материал. Займёт 1–3 минуты, получится новая версия. Опиши стиль или приложи картинку-образец."
        override val retexturePresets = listOf("Золото", "Камень", "Дерево", "Металл и ржавчина", "Мультяшный, яркие цвета", "Лёд")
        override val retexturePlaceholder = "Например: рыцарские доспехи из золота с красными вставками"
        override val retexturePicture = "Картинка-образец"
        override val retextureRemovePicture = "Убрать картинку"
        override fun retextureApply(cost: String?) = "Перекрасить" + (cost?.let { " · $it" } ?: "")
        override val retextureWorking = "Перекрашиваю модель — это займёт 1–3 минуты"
        override val editRetexture = "новая текстура"
        override fun editSize(studs: String) = "размер $studs"
        override fun editRotate(degrees: Int) = "поворот на $degrees°"
        override fun editTexture(size: Int) = "текстура $size px"
        override val editDebris = "убран мусор"
        override val working = "Меняю модель… это займёт до минуты"
        override fun saved(number: Int) = "Готово — сохранено как версия $number"
        override val failed = "Не получилось изменить модель. Попробуй ещё раз."
        override val unavailable = "Инструменты для моделей на этом сервере пока не подключены"
    }

    override val animationSetup = object : AnimationSetupStrings {
        override val characterTitle = "Персонаж"
        override val characterR15 = "Стандартный R15"
        override val characterR15Hint = "обычный аватар Roblox"
        override val characterCustom = "Свой персонаж"
        override val characterCustomHint = "Свой персонаж: открой 3D-работу → «Инструменты» → «Поставить кости»."
        override val characterOwnHint = "моя 3D-работа с костями"
        override val modeTitle = "Режим"
        override val modeNone = "Обычный"
        override val modeNoneHint = "танцы, ходьба, эмоции"
        override val modeFists = "Кулаки"
        override val modeFistsHint = "удары, блоки, увороты"
        override val modeOne = "Одно оружие"
        override val modeOneHint = "меч, пушка, посох, топор"
        override val modeTwo = "Два оружия"
        override val modeTwoHint = "комбо двумя руками"
        override val weaponTitle = "Оружие (правая рука)"
        override val offhandTitle = "Вторая рука"
        override val sword = "Меч"
        override val gun = "Пушка"
        override val staff = "Посох"
        override val axe = "Топор"
        override fun weaponHint(weapon: String) = when (weapon) { "GUN" -> "прицел и отдача"; "STAFF" -> "длинные махи"; "AXE" -> "тяжёлый, с замахом"; else -> "быстрые удары" }
        override val weaponModelTitle = "Модель оружия"
        override val weaponModelBuiltIn = "Встроенная"
        override val weaponModelBuiltInHint = "простая форма"
        override val weaponModelOwnHint = "моя 3D-работа"
        override val gripTitle = "Хват"
        override val gripOne = "Одной рукой"
        override val gripOneHint = "вторая рука свободна"
        override val gripTwo = "Двумя руками"
        override val gripTwoHint = "обе руки на рукояти"
        override val note = "Движения учитывают вес оружия: тяжёлое — с замахом и инерцией. Само оружие в руке появится в превью позже."
        override val noteWithWeapon = "Движения учитывают вес оружия: тяжёлое — с замахом и инерцией. В превью оружие в руке; в Studio плагин прикрепит к руке заготовку."
        override val openWorkspace = "Рабочая область"
        override val closeWorkspace = "Закрыть"
        override val clipTitle = "Клип"
        override val duration = "Длина"
        override val looped = "по кругу"
        override val once = "один раз"
        override val keys = "Ключи"
        override val jointsTitle = "Суставы сейчас"
        override val prevKey = "Предыдущий ключ"
        override val nextKey = "Следующий ключ"
        override val zoomIn = "Приблизить"
        override val zoomOut = "Отдалить"
        override val resetCamera = "Сбросить камеру"
        override val timeZoom = "Масштаб времени"
        override val cameraHint = "Колесо — зум · левая кнопка — вращение · правая или Shift — сдвиг"
        override val head = "Голова"
        override val torso = "Корпус"
        override val leftArm = "Левая рука"
        override val rightArm = "Правая рука"
        override val leftLeg = "Левая нога"
        override val rightLeg = "Правая нога"
        override val editTitle = "Доработать анимацию"
        override val editHint = "Опиши, что изменить, — остальное останется как есть. Каждая правка сохраняется версией."
        override val editPresets = listOf("Движения рук шире", "Быстрее", "Медленнее", "Плавнее", "Прыжок выше", "Быстрее вернуться в исходную позу")
        override val editPlaceholder = "Например: сделай взмах меча резче и с большим замахом"
        override fun editApply(cost: String?) = "Изменить" + (cost?.let { " · $it" } ?: "")
        override val editWorking = "Меняю анимацию…"
        override fun editDone(version: Int) = "Готово: версия $version"
        override val editFailed = "Не получилось изменить"
        override val versionsTitle = "Версии"
        override val versionOriginal = "исходная"
        override val restore = "Вернуть"
        override val libraryOpen = "Выбрать модель…"
        override val libraryTitle = "Модель персонажа и оружия"
        override val libraryDone = "Готово"
        override val libraryCharacterHint = "Персонажу нужны кости — загруженной модели они ставятся сами. Свою модель можно загрузить ниже."
        override val libraryWeaponHint = "Модель, которую персонаж держит в руке. Встроенная — меч, пушка, посох или топор из режима."
        override val libraryNoBones = "без костей"
        override val libraryEmpty = "Здесь появятся твои 3D-работы и загруженные модели."
        override val libraryAddBones = "Поставить кости (бесплатно)"
        override val libraryAddingBones = "Ставлю кости…"
        override val libraryTools = "Инструменты модели"
        override val libraryDownload = "Скачать .glb"
        override val libraryUpload = "Загрузить свою модель"
        override val libraryUploadHint = ".glb или .obj вместе с .mtl и текстурами, до 30 МБ. Нажми или перетащи файлы сюда."
        override val libraryUploading = "Загружаю и готовлю модель…"
        override val libraryFailed = "Не получилось"
        override fun libraryImported(name: String) = "Модель «$name» загружена"
        override val weaponTitleOne = "Оружие — встроенное или из «Моих работ»"
        override val rightHandTitle = "Правая рука"
        override val leftHandTitle = "Левая рука"
        override val handTitle = "Рука"
        override val handLeadTitle = "Ведущая рука"
        override val handRight = "Правая"
        override val handLeft = "Левая"
        override val handRightHint = "Оружие в правой руке"
        override val handLeftHint = "Оружие в левой руке: движения отражаются зеркально"
        override fun weaponMovesLike(kind: String) = "двигается как ${kind.lowercase()}"
    }

    override val quality = object : QualityStrings {
        override val sketchTitle = "Эскиз"
        override val meshTitle = "3D-модель"
        override val sketchStandard = "Быстрый"
        override val sketchStandardHint = "дёшево, но может дорисовать лишнее"
        override val sketchHigh = "Точный"
        override val sketchHighHint = "намного лучше слушается описания"
        override val meshStandard = "Обычное"
        override val meshStandardHint = "≈30 сек, для пробы и простых вещей"
        override val meshHigh = "Высокое"
        override val meshHighHint = "≈1 мин, чище форма и текстура"
        override val meshMax = "Максимальное"
        override val meshMaxHint = "≈3 мин, больше всего деталей"
        override val note = "Чем выше качество, тем больше кредитов. Выбирай под задачу — для пробы хватит обычного."
        override fun approve(cost: String) = "Сделать 3D · $cost"
        override val drewCheaper = "Точная модель эскизов сейчас недоступна — нарисовала быстрая, списана цена быстрого эскиза"
        override val variantsTitle = "Сколько эскизов"
        override val variantsOne = "1"
        override val variantsOneHint = "если идея уже ясна"
        override val variantsFour = "4 на выбор"
        override val variantsFourHint = "рисуются сразу, выбираешь лучший"
        override val pickVariant = "Нажми на эскиз, из которого делать 3D"
        override fun variantLabel(number: Int) = "Вариант $number"
        override val trianglesTitle = "Полигоны (треугольники)"
        override val trianglesMob = "Моб / декор"
        override val trianglesCharacter = "Игрок / персонаж"
        override val trianglesBoss = "Босс / крупный объект"
        override val trianglesHighPoly = "High-Poly"
        override val trianglesHighPolyHint = "без лимита, для скачивания; в Roblox — до 20 000"
        override val trianglesCustom = "Своё число"
        override fun trianglesUpTo(count: Int) = "до ${groupDigits(count, " ")}"
        override val trianglesNote = "Roblox MeshPart принимает до 20 000 треугольников. Больше треугольников — больше мелких деталей, но модель тяжелее для игры."
        override val textureTitle = "Текстура"
        override val texture1kHint = "1024 px — как в Roblox"
        override val texture2kHint = "2048 px — чётче в скачанной модели"
        override val texture4kHint = "4096 px — у «Высокого», в High-Poly модели"
        override val textureNote = "В Roblox всегда уходит текстура 1024 px — Studio сам уменьшает бóльшие. 2K и 4K нужны для скачивания и других программ."
    }

    override val accountData = object : AccountDataStrings {
        override val title = "Твои данные"
        override val hint = "Скачай всё, что сервис хранит о тебе, или удали аккаунт навсегда."
        override val download = "Скачать мои данные"
        override val delete = "Удалить аккаунт"
        override val deleteTitle = "Удалить аккаунт навсегда?"
        override val deleteWarning = "Будут удалены аккаунт, все твои работы, их версии, 3D-модели и эскизы. Неиспользованные кредиты сгорят. Отменить это нельзя."
        override val confirmLabel = "Введи свой email для подтверждения"
        override val confirmButton = "Удалить навсегда"
        override val emailMismatch = "Email не совпадает с аккаунтом"
        override val subscriptionActive = "Сначала отмени подписку на странице «Тарифы», потом удали аккаунт."
        override val deleted = "Аккаунт удалён"
        override val failed = "Не удалось удалить аккаунт. Попробуй ещё раз."
        override val adminDelete = "Удалить аккаунт"
        override val adminDeleted = "Аккаунт удалён"
        override fun adminConfirm(email: String) = "Удалить аккаунт $email со всеми работами? Отменить нельзя."
    }
}
